import { Alert, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { FloatInput, FloatSelect, showToast } from "src/componentes";
import { CTeEventoCadastroDto, tipoEventoCTeOptions } from "../CTe.Evento.Dto";
import yup from "src/validators/yup.Module";
import { yupResolver } from "@hookform/resolvers/yup";
import { CTeEventoIncluir } from "src/services/Fiscal.Service";
import { MapearErrosServidorCampo } from "src/hook/Functions";
import React, { useEffect } from "react";

interface Props {
  show: boolean;
  cteId: number;
  onClose: (show: boolean) => void;
  onReload: () => void;
}

const CTeEventoCorrecaoSchema = yup.object().shape({
  grupoAlterado: yup
    .string()
    .transform((v) => (v ? v.trim() : v))
    .required("Campo 'GrupoAlterado' é obrigatório.")
    .max(20, "Campo 'GrupoAlterado' deve ter no máximo 20 caracteres."),
  campoAlterado: yup
    .string()
    .transform((v) => (v ? v.trim() : v))
    .required("Campo 'CampoAlterado' é obrigatório.")
    .max(20, "Campo 'CampoAlterado' deve ter no máximo 20 caracteres."),
  valorAlterado: yup
    .string()
    .transform((v) => (v ? v.trim() : v))
    .required("Campo 'ValorAlterado' é obrigatório.")
    .max(500, "Campo 'ValorAlterado' deve ter no máximo 500 caracteres."),
  numeroItemAlterado: yup
    .number()
    .nullable()
    .transform((val, orig) =>
      orig === "" || orig === null || orig === undefined || isNaN(val) ? null : val
    )
    .notRequired()
    .min(1, "Campo 'NumeroItemAlterado' deve ser entre 1 e 99.")
    .max(99, "Campo 'NumeroItemAlterado' deve ser entre 1 e 99."),
});

const CTeEventoCadastroSchema = yup.object().shape({
  tipoEvento: yup
    .number()
    .typeError("O tipo de evento é obrigatório.")
    .required("O tipo de evento é obrigatório."),
  descricao: yup
    .string()
    .transform((v) => (v ? v.trim() : v))
    .when("tipoEvento", {
      is: (tipoEvento: number) => tipoEvento === 110111,
      then: (schema) =>
        schema
          .required("A justificativa do cancelamento é obrigatória.")
          .min(15, "A justificativa deve possuir no mínimo 15 caracteres.")
          .max(255, "A justificativa deve possuir no máximo 255 caracteres."),
      otherwise: (schema) => schema.notRequired(),
    }),
  correcoes: yup
    .array()
    .of(CTeEventoCorrecaoSchema)
    .when("tipoEvento", {
      is: (tipoEvento: number) => tipoEvento === 110110,
      then: (schema) =>
        schema
          .required("Para o tipo de evento 'Carta de Correção' informe ao menos uma correção.")
          .min(1, "Para o tipo de evento 'Carta de Correção' informe ao menos uma correção."),
      otherwise: (schema) => schema.notRequired(),
    }),
});

type FormType = yup.InferType<typeof CTeEventoCadastroSchema>;

const CTeEventoModalCadastro = (props: Props) => {
  const form = useForm<FormType>({
    resolver: yupResolver(CTeEventoCadastroSchema) as any,
    defaultValues: {
      tipoEvento: 110111,
      descricao: "",
      correcoes: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "correcoes" as never,
  });

  const tipoEventoSelecionado = Number(form.watch("tipoEvento"));

  useEffect(() => {
    if (tipoEventoSelecionado === 110110 && fields.length === 0) {
      append({
        grupoAlterado: "",
        campoAlterado: "",
        valorAlterado: "",
        numeroItemAlterado: null,
      });
    }
  }, [tipoEventoSelecionado, fields.length, append]);

  const onSubmit = async (data: FormType) => {
    const isCancelamento = Number(data.tipoEvento) === 110111;
    const isCartaCorrecao = Number(data.tipoEvento) === 110110;

    const dto: CTeEventoCadastroDto = {
      cTeId: props.cteId,
      tipoEvento: Number(data.tipoEvento),
      cancelamento: isCancelamento
        ? { justificativaCancelamento: data.descricao || "" }
        : null,
      correcoes: isCartaCorrecao && data.correcoes
        ? data.correcoes.map((c: any) => ({
            grupoAlterado: c.grupoAlterado,
            campoAlterado: c.campoAlterado,
            valorAlterado: c.valorAlterado,
            numeroItemAlterado: c.numeroItemAlterado ? Number(c.numeroItemAlterado) : null,
          }))
        : null,
    };

    try {
      const response = await CTeEventoIncluir(dto);
      if (response.data.success) {
        props.onClose(false);
        form.reset();
        showToast({
          message: "Evento incluído com sucesso",
          title: "Sucesso",
          variant: "Success",
        });
        props.onReload();
      } else {
        MapearErrosServidorCampo(response.data.notifications, form.setError);
        response.data.notifications.forEach((item) => {
          showToast({
            message: item.key ? `${item.key}: ${item.message}` : item.message,
            title: "Erro",
            variant: "Danger",
          });
        });
      }
    } catch (error) {
      console.error("Erro ao incluir evento:", error);
      showToast({
        message: "Ocorreu erro ao incluir evento",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const onCloseHandler = () => {
    form.reset();
    props.onClose(false);
  };

  return (
    <Modal
      show={props.show}
      centered
      size={tipoEventoSelecionado === 110110 ? "lg" : undefined}
      onHide={onCloseHandler}
    >
      <Form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Incluir Evento de CT-e</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col xs={12}>
              <FloatSelect
                label="Tipo de Evento"
                options={[
                  { label: "Selecione", value: "" },
                  ...tipoEventoCTeOptions,
                ]}
                register={form.register("tipoEvento")}
                errorsMessage={form.formState.errors.tipoEvento}
              />
            </Col>
          </Row>

          {tipoEventoSelecionado === 110111 && (
            <Row>
              <Col xs={12}>
                <FloatInput
                  label="Justificativa do Cancelamento"
                  inputMask="textarea"
                  maxLength={255}
                  registerField={form.register("descricao")}
                  errorsMessage={form.formState.errors.descricao}
                />
              </Col>
            </Row>
          )}

          {tipoEventoSelecionado === 110110 && (
            <>
              <Alert variant="warning" className="mb-3 py-2 px-3 small">
                <strong>Atenção:</strong> A Carta de Correção (CC-e) <u>não</u> pode ser utilizada para corrigir:
                <ol className="mb-0 ps-3 mt-1">
                  <li>Variáveis que determinam o valor do imposto (base de cálculo, alíquota, valor da prestação).</li>
                  <li>Dados cadastrais que impliquem mudança completa do emitente, tomador, remetente ou destinatário.</li>
                  <li>Data de emissão ou de saída.</li>
                </ol>
              </Alert>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Correções do CT-e</h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  type="button"
                  onClick={() =>
                    append({
                      grupoAlterado: "",
                      campoAlterado: "",
                      valorAlterado: "",
                      numeroItemAlterado: null,
                    })
                  }
                >
                  + Adicionar Correção
                </Button>
              </div>

              {form.formState.errors.correcoes?.message && (
                <div className="text-danger small mb-2">
                  {form.formState.errors.correcoes.message as string}
                </div>
              )}

              {fields.map((field, index) => {
                const itemErrors = (form.formState.errors.correcoes as any)?.[index];
                return (
                  <Card key={field.id} className="mb-3 border">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="fw-bold text-secondary">
                          Item #{index + 1}
                        </small>
                        {fields.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            type="button"
                            onClick={() => remove(index)}
                          >
                            Remover
                          </Button>
                        )}
                      </div>
                      <Row className="g-2">
                        <Col xs={12} md={4}>
                          <FloatInput
                            label="Grupo Alterado (ex: ide, rem)"
                            maxLength={20}
                            registerField={form.register(
                              `correcoes.${index}.grupoAlterado` as const
                            )}
                            errorsMessage={itemErrors?.grupoAlterado}
                          />
                        </Col>
                        <Col xs={12} md={4}>
                          <FloatInput
                            label="Campo Alterado (ex: xObs, CFOP)"
                            maxLength={20}
                            registerField={form.register(
                              `correcoes.${index}.campoAlterado` as const
                            )}
                            errorsMessage={itemErrors?.campoAlterado}
                          />
                        </Col>
                        <Col xs={12} md={4}>
                          <FloatInput
                            label="Nº Item Alterado (Opcional)"
                            type="number"
                            registerField={form.register(
                              `correcoes.${index}.numeroItemAlterado` as const
                            )}
                            errorsMessage={itemErrors?.numeroItemAlterado}
                          />
                        </Col>
                        <Col xs={12}>
                          <FloatInput
                            label="Valor Alterado"
                            maxLength={500}
                            registerField={form.register(
                              `correcoes.${index}.valorAlterado` as const
                            )}
                            errorsMessage={itemErrors?.valorAlterado}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" type="submit">
            Confirmar
          </Button>
          <Button variant="outline-secondary" onClick={onCloseHandler}>
            Sair
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CTeEventoModalCadastro;
